import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { Avatar } from '../avatar/avatar';
import { EntityVisualComponent } from '../entity-visual/entity-visual';
import { ZagIcon } from '../icon/icon';
import type { DropdownOption } from '../dropdown/dropdown';

export type MultiSelectSize = 'sm' | 'md' | 'lg';

/**
 * Multi-select dropdown. Stores `string[]` values. Renders the current
 * selection as removable chips in the trigger, opens an overlay panel with
 * a checkable option list and an optional search field.
 *
 * Use for fixed option lists (multi-select custom-property type) or for
 * multi-reference custom properties where the option list is a small set of
 * locations / skills / etc.
 *
 * @example
 * ```html
 * <zag-multi-select
 *   [options]="languageOptions"
 *   [formControl]="languagesControl"
 *   [searchable]="true"
 *   placeholder="Select languages"
 * />
 * ```
 */
@Component({
  selector: 'zag-multi-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OverlayModule, Avatar, EntityVisualComponent, ZagIcon],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelect),
      multi: true,
    },
  ],
  host: {
    class: 'zag-multi-select',
    '[class.zag-multi-select--open]': 'isOpen()',
    '[class.zag-multi-select--disabled]': 'isDisabled()',
    '[class.zag-multi-select--sm]': 'size() === "sm"',
    '[class.zag-multi-select--lg]': 'size() === "lg"',
  },
  styles: [
    `
      :host {
        display: block;
        position: relative;
        width: 100%;
        font-family: var(--font-family-primary);
      }

      .zag-multi-select__control {
        display: flex;
        position: relative;
        align-items: center;
        gap: var(--spacing-sm);
        min-height: 2.5rem;
        width: 100%;
        padding: var(--spacing-xs) var(--spacing-md);
        background-color: var(--input-background);
        border: var(--border-width-default) solid var(--input-border);
        border-radius: var(--border-radius-sm);
        color: var(--input-text);
        font-size: var(--font-size-control);
        line-height: var(--line-height-normal);
        cursor: pointer;
        flex-wrap: wrap;
        text-align: left;
        transition:
          border-color var(--transition-duration-fast) var(--transition-timing),
          box-shadow var(--transition-duration-fast) var(--transition-timing);
      }

      .zag-multi-select__trigger {
        all: unset;
        display: flex;
        position: absolute;
        inset: 0;
        box-sizing: border-box;
        align-items: center;
        padding: var(--spacing-xs) var(--spacing-md);
        cursor: pointer;
      }

      :host(.zag-multi-select--disabled) .zag-multi-select__trigger {
        cursor: not-allowed;
      }

      :host:not(.zag-multi-select--disabled) .zag-multi-select__control:hover {
        border-color: var(--input-border-hover);
      }

      .zag-multi-select__control:focus-within {
        outline: 0;
        border-color: var(--input-border-focus);
        box-shadow: var(--shadow-sm);
      }

      :host(.zag-multi-select--open) .zag-multi-select__control {
        border-color: var(--input-border-focus);
        box-shadow: var(--shadow-sm);
      }

      :host(.zag-multi-select--disabled) .zag-multi-select__control {
        background-color: var(--surface-color-disabled);
        cursor: not-allowed;
        opacity: var(--opacity-disabled);
      }

      .zag-multi-select__placeholder {
        color: var(--input-placeholder);
        flex: 1;
      }

      .zag-multi-select__chips {
        display: flex;
        position: relative;
        z-index: 1;
        flex-wrap: wrap;
        gap: var(--spacing-xs);
        flex: 1;
        min-width: 0;
        pointer-events: none;
      }

      .zag-multi-select__chip {
        display: inline-flex;
        align-items: center;
        gap: var(--spacing-xs);
        padding: 2px var(--spacing-sm);
        border-radius: var(--border-radius-sm);
        background-color: var(--background-color-secondary);
        color: var(--text-color-primary);
        font-size: var(--font-size-small);
        max-width: 100%;
      }

      .zag-multi-select__chip-label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .zag-multi-select__chip zag-avatar {
        flex-shrink: 0;
      }

      .zag-multi-select__chip-remove {
        all: unset;
        cursor: pointer;
        line-height: 0;
        padding: 0 2px;
        border-radius: var(--border-radius-sm);
        color: var(--text-color-secondary);
        transition: color var(--transition-duration-fast) var(--transition-timing);
        pointer-events: auto;
      }

      .zag-multi-select__chip-remove:hover {
        color: var(--text-color-primary);
      }

      .zag-multi-select__chip-remove:focus-visible,
      .zag-multi-select__clear:focus-visible {
        outline: var(--border-width-default) solid var(--input-border-focus);
        outline-offset: var(--border-width-default);
      }

      .zag-multi-select__actions {
        display: inline-flex;
        position: relative;
        z-index: 1;
        align-items: center;
        gap: var(--spacing-xs);
        margin-left: auto;
        pointer-events: none;
      }

      .zag-multi-select__clear,
      .zag-multi-select__arrow {
        all: unset;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: var(--icon-size-secondary);
        height: var(--icon-size-secondary);
        border-radius: var(--border-radius-sm);
        color: var(--text-color-secondary);
        cursor: pointer;
        transition:
          color var(--transition-duration-fast) var(--transition-timing),
          transform var(--transition-duration-fast) var(--transition-timing);
      }

      .zag-multi-select__clear:hover,
      .zag-multi-select__arrow:hover {
        color: var(--text-color-primary);
      }

      .zag-multi-select__clear {
        pointer-events: auto;
      }

      :host(.zag-multi-select--open) .zag-multi-select__arrow {
        transform: rotate(180deg);
      }

      .zag-multi-select__panel {
        min-width: 12rem;
        max-height: 18rem;
        overflow-y: auto;
        background-color: var(--surface-color-default);
        border: var(--border-width-default) solid var(--border-color-default);
        border-radius: var(--border-radius-sm);
        box-shadow: var(--shadow-md);
        padding: var(--spacing-xs) 0;
        font-family: var(--font-family-primary);
      }

      .zag-multi-select__search {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        position: sticky;
        top: 0;
        z-index: 1;
        padding: var(--spacing-sm) var(--spacing-md);
        background-color: var(--surface-color-default);
        border-bottom: var(--border-width-default) solid var(--border-color-default);
      }

      .zag-multi-select__search-input {
        all: unset;
        flex: 1;
        min-width: 0;
        font-size: var(--font-size-control);
        color: var(--input-text);
      }

      .zag-multi-select__search-input::placeholder {
        color: var(--input-placeholder);
      }

      .zag-multi-select__search-input:focus {
        outline: var(--border-width-default) solid var(--input-border-focus);
        outline-offset: var(--border-width-default);
      }

      .zag-multi-select__option {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-sm) var(--spacing-md);
        font-size: var(--font-size-control);
        color: var(--text-color-primary);
        cursor: pointer;
        user-select: none;
        transition: background-color var(--transition-duration-fast) var(--transition-timing);
      }

      .zag-multi-select__option:hover:not(.zag-multi-select__option--disabled),
      .zag-multi-select__option--highlighted:not(.zag-multi-select__option--disabled) {
        background-color: var(--surface-color-hover);
      }

      .zag-multi-select__option:focus-visible {
        outline: var(--border-width-default) solid var(--input-border-focus);
      }

      .zag-multi-select__option--disabled {
        color: var(--text-color-secondary);
        opacity: var(--opacity-disabled);
        cursor: not-allowed;
      }

      .zag-multi-select__option-check {
        width: var(--icon-size-secondary);
        height: var(--icon-size-secondary);
        border: var(--border-width-default) solid var(--border-color-strong);
        border-radius: var(--border-radius-sm);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: transparent;
        flex-shrink: 0;
      }

      .zag-multi-select__option--selected {
        background-color: var(--color-primary-alt);
      }

      .zag-multi-select__option--selected .zag-multi-select__option-check {
        background-color: var(--color-primary);
        border-color: var(--color-primary);
        color: var(--color-white);
      }

      .zag-multi-select__option-label {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .zag-multi-select__no-results {
        padding: var(--spacing-sm);
        text-align: center;
        color: var(--text-color-tertiary);
        font-size: var(--font-size-sm);
      }
    `,
  ],
  template: `
    <div class="zag-multi-select__control">
      <button
        type="button"
        class="zag-multi-select__trigger"
        cdkOverlayOrigin
        #triggerOrigin="cdkOverlayOrigin"
        [id]="triggerId()"
        [attr.aria-label]="triggerAriaLabel()"
        [attr.aria-labelledby]="ariaLabelledby()?.trim() || null"
        [attr.aria-haspopup]="'listbox'"
        [attr.aria-controls]="listboxId()"
        [attr.aria-expanded]="isOpen()"
        [attr.aria-disabled]="isDisabled()"
        [disabled]="isDisabled()"
        (click)="toggle()"
        (keydown.arrowdown)="openAndFocusFirstOption($event)"
        (blur)="onBlur()"
      >
        @if (selectedOptions().length === 0) {
          <span class="zag-multi-select__placeholder">{{ placeholder() }}</span>
        }
      </button>
      @if (selectedOptions().length > 0) {
        <span class="zag-multi-select__chips">
          @for (option of selectedOptions(); track option.value) {
            <span class="zag-multi-select__chip">
              @if (option.imageUrl || option.avatarName || option.avatarSrc) {
                <zag-avatar [name]="option.avatarName ?? option.label" [src]="option.avatarSrc ?? option.imageUrl ?? null" size="xs" />
              } @else if (option.icon || option.color) {
                <zag-entity-visual [icon]="option.icon" [color]="option.color" [imageUrl]="option.imageUrl" size="sm" />
              }
              <span class="zag-multi-select__chip-label">{{ option.label }}</span>
              @if (!isDisabled()) {
                <button
                  type="button"
                  #chipRemove
                  class="zag-multi-select__chip-remove"
                  [attr.aria-label]="removeAriaLabel() + ' ' + option.label"
                  (click)="removeOption(option, $event)"
                >
                  <span zagIcon="close" style="font-size: 0.875rem"></span>
                </button>
              }
            </span>
          }
        </span>
      }
      <span class="zag-multi-select__actions">
        @if (showClearButton()) {
          <button type="button" class="zag-multi-select__clear" [attr.aria-label]="clearAriaLabel()" (click)="clearAll($event)">
            <span zagIcon="close"></span>
          </button>
        }
        <span class="zag-multi-select__arrow" aria-hidden="true">
          <span [zagIcon]="isOpen() ? 'expand_less' : 'expand_more'"></span>
        </span>
      </span>
    </div>

    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="triggerOrigin"
      [cdkConnectedOverlayOpen]="isOpen()"
      [cdkConnectedOverlayHasBackdrop]="true"
      [cdkConnectedOverlayBackdropClass]="'zag-dropdown-backdrop'"
      [cdkConnectedOverlayPositions]="overlayPositions"
      (attach)="onOverlayAttach()"
      (backdropClick)="close()"
      (detach)="close()"
    >
      <div
        class="zag-multi-select__panel"
        [id]="listboxId()"
        [style.width.px]="triggerOrigin.elementRef.nativeElement.offsetWidth"
        role="listbox"
        [attr.aria-labelledby]="triggerId()"
        aria-multiselectable="true"
        tabindex="-1"
        (keydown.escape)="closeAndFocusTrigger($event)"
      >
        @if (searchable()) {
          <div class="zag-multi-select__search">
            <span [zagIcon]="'search'"></span>
            <input
              #searchInput
              type="text"
              class="zag-multi-select__search-input"
              autocomplete="off"
              [attr.aria-label]="searchAriaLabel()"
              [placeholder]="searchPlaceholder()"
              [value]="searchTerm()"
              (input)="searchTerm.set($any($event.target).value)"
              (click)="$event.stopPropagation()"
            />
          </div>
        }

        @for (option of displayedOptions(); track option.value) {
          <div
            #optionElement
            class="zag-multi-select__option"
            [class.zag-multi-select__option--selected]="isSelected(option.value)"
            [class.zag-multi-select__option--disabled]="option.disabled"
            role="option"
            [attr.aria-selected]="isSelected(option.value)"
            [attr.aria-disabled]="option.disabled"
            (click)="toggleOption(option)"
            (keydown.enter)="toggleOption(option)"
            (keydown.space)="toggleOption(option); $event.preventDefault()"
            (keydown.arrowdown)="focusAdjacentEnabledOption(option, 1, $event)"
            (keydown.arrowup)="focusAdjacentEnabledOption(option, -1, $event)"
            [attr.tabindex]="option.disabled ? null : 0"
          >
            <span class="zag-multi-select__option-check" aria-hidden="true">
              <span [zagIcon]="'check'" style="font-size: 0.875rem"></span>
            </span>
            @if (option.imageUrl || option.avatarName || option.avatarSrc) {
              <zag-avatar [name]="option.avatarName ?? option.label" [src]="option.avatarSrc ?? option.imageUrl ?? null" size="sm" />
            } @else if (option.icon || option.color) {
              <zag-entity-visual [icon]="option.icon" [color]="option.color" [imageUrl]="option.imageUrl" size="sm" />
            }
            <span class="zag-multi-select__option-label">{{ option.label }}</span>
          </div>
        } @empty {
          <div class="zag-multi-select__no-results">{{ noResultsText() }}</div>
        }
      </div>
    </ng-template>
  `,
})
export class MultiSelect implements ControlValueAccessor {
  private static nextId = 0;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly generatedId = `zag-multi-select-${MultiSelect.nextId++}`;

  @ViewChild('triggerOrigin', { read: ElementRef }) triggerRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChildren('optionElement') optionElements?: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('chipRemove') chipRemoveElements?: QueryList<ElementRef<HTMLButtonElement>>;

  readonly options = input.required<DropdownOption<string>[]>();
  readonly placeholder = input<string>('Select…');
  readonly disabled = input<boolean>(false);
  readonly id = input<string | undefined>(undefined);
  readonly ariaLabel = input<string | undefined>(undefined);
  readonly ariaLabelledby = input<string | undefined>(undefined);
  readonly required = input<boolean>(false);
  readonly size = input<MultiSelectSize>('md');
  readonly clearable = input<boolean>(true);
  readonly searchable = input<boolean>(false);
  readonly searchPlaceholder = input<string>('Search…');
  readonly searchAriaLabel = input<string>('Filter options');
  readonly noResultsText = input<string>('No results');
  readonly clearAriaLabel = input<string>('Clear selection');
  readonly removeAriaLabel = input<string>('Remove');

  protected readonly isOpen = signal<boolean>(false);
  protected readonly searchTerm = signal<string>('');
  private readonly cvaDisabled = signal(false);
  private readonly selectedValues = signal<readonly string[]>([]);
  protected readonly isDisabled = computed(() => this.disabled() || this.cvaDisabled());
  protected readonly triggerId = computed(() => this.id()?.trim() || this.generatedId);
  protected readonly listboxId = computed(() => `${this.triggerId()}-listbox`);

  protected readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  readonly selectedOptions = computed(() => {
    const values = new Set(this.selectedValues());
    return this.options().filter(opt => values.has(opt.value));
  });

  readonly displayedOptions = computed(() => {
    if (!this.searchable()) return this.options();
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) return this.options();
    return this.options().filter(opt => opt.label.toLowerCase().includes(term));
  });

  readonly showClearButton = computed(() => this.clearable() && this.selectedValues().length > 0 && !this.isDisabled());
  protected readonly triggerAriaLabel = computed(() =>
    this.ariaLabelledby()?.trim()
      ? null
      : this.ariaLabel()?.trim() ||
        this.selectedOptions()
          .map(option => option.label)
          .join(', ') ||
        this.placeholder()
  );

  constructor() {
    effect(() => {
      if (this.isDisabled()) this.close();
    });
  }

  isSelected(value: string): boolean {
    return this.selectedValues().includes(value);
  }

  private onChange: (value: string[]) => void = () => {
    // default no-op
  };
  private onTouched: () => void = () => {
    // default no-op
  };

  writeValue(value: string[] | null | undefined): void {
    this.selectedValues.set(Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled.set(isDisabled);
    if (isDisabled) this.close();
  }

  toggle(): void {
    if (this.isDisabled()) return;
    this.isOpen.update(open => !open);
    if (!this.isOpen()) {
      this.searchTerm.set('');
    }
  }

  openAndFocusFirstOption(event: Event): void {
    event.preventDefault();
    if (this.isDisabled()) return;
    if (this.isOpen()) {
      this.focusFirstEnabledOption();
      return;
    }
    this.isOpen.set(true);
  }

  onOverlayAttach(): void {
    queueMicrotask(() => {
      if (!this.isOpen() || this.isDisabled()) return;
      if (this.searchable()) {
        this.searchInputRef?.nativeElement.focus();
        return;
      }
      this.focusFirstEnabledOption();
    });
  }

  close(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.searchTerm.set('');
    this.onTouched();
  }

  closeAndFocusTrigger(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.close();
    this.triggerRef?.nativeElement.focus();
  }

  onBlur(): void {
    if (!this.isOpen()) this.onTouched();
  }

  private focusFirstEnabledOption(): void {
    const firstEnabledIndex = this.displayedOptions().findIndex(option => !option.disabled);
    this.optionElements?.get(firstEnabledIndex)?.nativeElement.focus();
  }

  focusAdjacentEnabledOption(option: DropdownOption<string>, direction: 1 | -1, event: Event): void {
    event.preventDefault();
    if (this.isDisabled()) return;

    const options = this.displayedOptions();
    let index = options.findIndex(candidate => candidate.value === option.value);
    while ((index += direction) >= 0 && index < options.length) {
      if (!options[index].disabled) {
        this.optionElements?.get(index)?.nativeElement.focus();
        return;
      }
    }
  }

  toggleOption(option: DropdownOption<string>): void {
    if (this.isDisabled() || option.disabled) return;
    const current = this.selectedValues();
    const next = current.includes(option.value) ? current.filter(v => v !== option.value) : [...current, option.value];
    this.selectedValues.set(next);
    this.onChange([...next]);
  }

  removeOption(option: DropdownOption<string>, event: Event): void {
    event.stopPropagation();
    if (this.isDisabled()) return;
    const removedIndex = this.selectedOptions().findIndex(candidate => candidate.value === option.value);
    const restoreFocus = document.activeElement === event.currentTarget;
    const next = this.selectedValues().filter(v => v !== option.value);
    this.selectedValues.set(next);
    this.onChange([...next]);
    if (restoreFocus) {
      queueMicrotask(() =>
        (
          this.chipRemoveElements?.get(Math.min(removedIndex, this.chipRemoveElements.length - 1))?.nativeElement ??
          this.triggerRef?.nativeElement
        )?.focus()
      );
    }
  }

  clearAll(event: Event): void {
    event.stopPropagation();
    if (this.isDisabled()) return;
    const restoreFocus = document.activeElement === event.currentTarget;
    this.selectedValues.set([]);
    this.onChange([]);
    if (restoreFocus) queueMicrotask(() => this.triggerRef?.nativeElement.focus());
  }
}
